import { useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Box, Button, Skeleton, Typography } from "@mui/material";

import { useProfileData } from "../../../../hooks/useProfileData";
import { getGinoChatRoute } from "../../../../routers/routeUtils";

import GinoChat from "../../components/GinoChat";
import { GinoCreditsData } from "../../api/services/ginoCredits";
import {
  GinoMainSectionIcon,
  HireProfessionalIcon,
  ScoutPlayerIcon,
  TrialPrepTryoutIcon,
  NutritionPlanIcon,
  TrainingPlanIcon,
} from "../../assets/icons/gino-icons";

export const GinoHomeChat = ({
  ginoCredits,
  isLoadingGinoCredits,
  handleOpenCreditLeftModal,
}: {
  ginoCredits?: GinoCreditsData;
  isLoadingGinoCredits: boolean;
  handleOpenCreditLeftModal: () => void;
}) => {
  const navigate = useNavigate();
  const ref = useRef<HTMLInputElement>(null);
  const { t } = useTranslation("gino", { keyPrefix: "homePage" });
  const {
    data: { fullName: name },
  } = useProfileData();

  const handleScoutAPlayer = () => {
    if (ginoCredits?.balance !== undefined && ginoCredits?.balance <= 5) {
      handleOpenCreditLeftModal();
    } else {
      navigate(getGinoChatRoute("player"));
    }
  };
  const handleHireAProfessional = () => {
    if (ginoCredits?.balance !== undefined && ginoCredits?.balance <= 5) {
      handleOpenCreditLeftModal();
    } else {
      navigate(getGinoChatRoute("professional"));
    }
  };
  const handleTrainingPlan = () => {
    if (ginoCredits?.balance !== undefined && ginoCredits?.balance <= 5) {
      handleOpenCreditLeftModal();
    } else {
      navigate(getGinoChatRoute("trainingPlan"));
    }
  };
  const handleNutritionPlan = () => {
    if (ginoCredits?.balance !== undefined && ginoCredits?.balance <= 5) {
      handleOpenCreditLeftModal();
    } else {
      navigate(getGinoChatRoute("nutritionPlan"));
    }
  };
  const handleTrialPrepTryout = () => {
    if (ginoCredits?.balance !== undefined && ginoCredits?.balance <= 5) {
      handleOpenCreditLeftModal();
    } else {
      navigate(getGinoChatRoute("trialPrepTryout"));
    }
  };

  const handleOnKeyDownWithinChatInput = async (event: React.KeyboardEvent) => {
    if (event.key !== "Enter") return;
    event.preventDefault();
    handleOnSend(ref.current?.value ?? "");
  };

  const handleOnSend = (message: string) => {
    if (ginoCredits?.balance !== undefined && ginoCredits?.balance <= 5) {
      handleOpenCreditLeftModal();
    } else {
      navigate(getGinoChatRoute(), { state: { message } });
    }
  };

  return (
    <Box sx={styles.container}>
      <GinoMainSectionIcon />
      <Box sx={styles.textContainer}>
        <Typography sx={styles.title}>{t("hello", { name })}</Typography>
        <Typography sx={styles.subtitle}>{t("meetGino")}</Typography>
      </Box>
      <GinoChat
        placeholder={t("letsMakeTodayCounts")}
        onSend={handleOnSend}
        handleOnKeyDownWithinChatInput={handleOnKeyDownWithinChatInput}
        chatInputRef={ref}
        ginoCredits={ginoCredits}
        isLoadingGinoCredits={isLoadingGinoCredits}
        handleOpenCreditLeftModal={handleOpenCreditLeftModal}
      />
      <Box sx={styles.chatActionsButtonsContainer}>
        <Button
          sx={styles.chatActionsButton}
          startIcon={<ScoutPlayerIcon />}
          onClick={handleScoutAPlayer}
          disabled={isLoadingGinoCredits}
        >
          {isLoadingGinoCredits && <Skeleton variant="rounded" width={100} height={14} />}
          {!isLoadingGinoCredits && t("scoutAPlayer")}
        </Button>
        <Button
          sx={styles.chatActionsButton}
          startIcon={<HireProfessionalIcon />}
          onClick={handleHireAProfessional}
          disabled={isLoadingGinoCredits}
        >
          {isLoadingGinoCredits && <Skeleton variant="rounded" width={100} height={14} />}
          {!isLoadingGinoCredits && t("hireAProfessional")}
        </Button>
        <Button
          sx={styles.chatActionsButton}
          startIcon={<TrainingPlanIcon />}
          onClick={handleTrainingPlan}
          disabled={isLoadingGinoCredits}
        >
          {isLoadingGinoCredits && <Skeleton variant="rounded" width={100} height={14} />}
          {!isLoadingGinoCredits && t("trainingPlan")}
        </Button>
        <Button
          sx={styles.chatActionsButton}
          startIcon={<NutritionPlanIcon />}
          onClick={handleNutritionPlan}
          disabled={isLoadingGinoCredits}
        >
          {isLoadingGinoCredits && <Skeleton variant="rounded" width={100} height={14} />}
          {!isLoadingGinoCredits && t("nutritionPlan")}
        </Button>
        <Button
          sx={styles.chatActionsButton}
          startIcon={<TrialPrepTryoutIcon />}
          onClick={handleTrialPrepTryout}
          disabled={isLoadingGinoCredits}
        >
          {isLoadingGinoCredits && <Skeleton variant="rounded" width={100} height={14} />}
          {!isLoadingGinoCredits && t("trialPrepTryout")}
        </Button>
      </Box>
    </Box>
  );
};

const styles = {
  container: {
    display: "flex",
    flexDirection: "column",
    gap: "24px",
  },
  textContainer: {
    display: "flex",
    flexDirection: "column",
    gap: "2px",
  },
  title: {
    color: "var(--text-base-primary, #14151A)",
    fontFamily: "Montserrat",
    fontSize: "var(--typography-size-h6, 24px)",
    fontStyle: "normal",
    fontWeight: 600,
    lineHeight: "var(--typography-line-height-h6, 32px)",
  },
  subtitle: {
    color: "var(--Color-Gray-400, #9CA3AF)",
    fontFamily: "Montserrat",
    fontSize: "var(--typography-size-h6, 24px)",
    fontStyle: "normal",
    fontWeight: 600,
    lineHeight: "var(--typography-line-height-h6, 32px)",
    letterSpacing: "var(--typography-letter-spacing-h6, -0.3px)",
  },

  chatActionsButtonsContainer: {
    width: "80%",
    display: "flex",
    flexWrap: "wrap",
    gap: "8px",
    justifyContent: "center",
    margin: "0 auto",
  },
  chatActionsButton: {
    padding: "12px 16px",
    borderRadius: "32px",
    background: "var(--Color-Gray-100, #F3F4F6) !important",
    color: "var(--Color-Gray-700, #374151) !important",
    fontFamily: "Montserrat",
    fontSize: "14px",
    fontStyle: "normal",
    fontWeight: 500,
    lineHeight: "150%",
    letterSpacing: "-0.28px",
  },
};
